<template>
    <!-- Unified Custom PrimeVueDialog for Alert, Confirm, and Prompt -->
    <PrimeVueDialog
        v-model:visible="visible"
        :header="header"
        modal
        :style="{ width: '450px' }"
        append-to="body"
        :focus-on-hide="false"
        :block-scroll="false"
        :pt="{
            'title': {
                'style': 'font-weight: 600; font-size: 1.125rem; padding-left: 0.5rem'
            }
        }"
        @after-hide="restoreFocus"
    >
        <div class="tw:pl-2">
            <div v-if="message" v-html="message" />

            <InputText
                v-if="type === 'prompt'"
                v-bind="inputAttrs"
                v-model="inputValue"
                :class="[{ 'p-invalid': validationError }, 'tw:text-left']"
                @keyup.enter="handleConfirm"
                @input="validationError = false"
            />
            <small v-if="validationError" class="tw:text-red-500 tw:text-sm">Value is required.</small>
        </div>
        <template #footer>
            <PrimeVueButton v-if="showCancel" :label="cancelText" class="p-button-text" @click="handleCancel" />
            <PrimeVueButton :label="confirmText" :class="confirmButtonClass" @click="handleConfirm" />
        </template>
    </PrimeVueDialog>
</template>

<script>
import PrimeVueButton from 'primevue/button';
import PrimeVueDialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';

export default {
    name: 'GlobalDialogs',
    components: { PrimeVueDialog, InputText, PrimeVueButton },
    data() {
        return {
            visible: false,
            type: 'alert', // alert, confirm, prompt
            header: '',
            message: '',
            inputValue: '',
            inputAttrs: {},
            confirmText: 'OK',
            cancelText: 'Cancel',
            confirmButtonClass: '',
            onConfirm: null,
            onCancel: null,
            triggerElement: null,
            scrollPosition: 0,
            validationError: false,
            /** 'confirm' | 'cancel' | null — how a confirm dialog was closed (null = mask/X/Escape). */
            dialogDismissType: null,
        };
    },
    computed: {
        showCancel() {
            return this.type === 'confirm' || this.type === 'prompt';
        },
    },
    mounted() {
        window.addEventListener('primevue-dialog-request', this.handleRequest);
    },
    beforeUnmount() {
        window.removeEventListener('primevue-dialog-request', this.handleRequest);
    },
    methods: {
        handleRequest(event) {
            this.scrollPosition = window.scrollY;
            this.triggerElement = document.activeElement;
            const params = event.detail || {};
            this.type = params.type || 'alert';
            this.header = params.title || this.type.charAt(0).toUpperCase() + this.type.slice(1);
            this.message = params.message || '';
            this.inputValue = params.inputAttrs?.value || '';
            const { value, ...restAttrs } = params.inputAttrs || {};
            this.inputAttrs = restAttrs;
            this.confirmText = params.confirmText || 'OK';
            this.cancelText = params.cancelText || 'Cancel';
            this.confirmButtonClass = params.confirmButtonClass || '';
            this.onConfirm = params.onConfirm;
            this.onCancel = params.onCancel;
            this.dialogDismissType = null;
            this.visible = true;
            this.validationError = false;
        },
        handleConfirm() {
            if (this.type === 'prompt' && !this.inputValue) {
                this.validationError = true;
                return;
            }

            this.dialogDismissType = 'confirm';

            if (this.onConfirm) {
                if (this.type === 'prompt') {
                    this.onConfirm(this.inputValue);
                } else {
                    this.onConfirm();
                }
            }
            this.visible = false;
        },
        handleCancel() {
            this.dialogDismissType = 'cancel';
            if (this.onCancel) this.onCancel();
            this.visible = false;
        },
        restoreFocus() {
            if (this.type === 'confirm' && this.dialogDismissType === null && this.onCancel) {
                this.onCancel();
            }
            this.dialogDismissType = null;

            const savedScrollY = this.scrollPosition;
            this.$nextTick(() => {
                if (this.triggerElement && typeof this.triggerElement.focus === 'function') {
                    this.triggerElement.focus();
                }
                window.scrollTo(0, savedScrollY);
            });
        },
    },
};
</script>
